import React, { useContext, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import { TranslationContext, countries } from "../../../umbilical_tools";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import {
  DatepickerField,
  POIAutoCompleteInputField,
  SelectField,
} from "../../forms/inputGroup/helpers";
import jssVariables from "../../../jss_setup";
import {
  carRentalFiltersReset,
  carRentalSearchFormReset,
  carRentalSearchResultsReset,
  selectedCarRentalReset,
} from "../../../actions/search/car_rental_search";
import { validateCarRentalSearchForm } from "./helpers";
import Toggle from "../../forms/inputGroup/helpers/Toggle";
import { DriverAge } from "./components";
import { useCarRentalsInitSearch } from "../../../hooks/carRental";
import { SearchButton } from "../../common/Button";

const pointInputGroupStyles = {
  differentDropOff: {
    singleValue: {
      smallscreen: {
        maxWidth: "14rem",
      },
    },
  },
  sameDropOff: {
    singleValue: {
      smallscreen: {
        maxWidth: "27rem",
      },
    },
  },
};
const styles = createUseStyles({
  carRentalSearchForm: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    padding: jssVariables.normal_gap,
    ..._.get(props, "customStyles.carRentalSearchForm", {}),
  }),
  formContainer: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.formContainer", {}),
  }),
  searchBtnContainer: (props) => ({
    ..._.get(props, "customStyles.searchBtnContainer", {}),
  }),
  checkbox: (props) => ({
    color: "red",
    ..._.get(props, "customStyles.checkbox", {}),
  }),
  mobileOnly: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.mobileOnly", {}),
  }),
  desktopOnly: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.desktopOnly", {}),
  }),
  desktop: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.desktop", {}),
  }),
  searchBtnContr: (props) => ({
    justifySelf: "center",
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  span2: (props) => ({
    gridColumn: "span 2",
    ..._.get(props, "customStyles.span2", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    carRentalSearchForm: (props) => ({
      gridTemplateColumns: "55rem max-content",
      alignItems: "end",
      padding: 0,
      ..._.get(props, "customStyles.carRentalSearchForm.smallscreen", {}),
    }),
    formContainer: (props) => ({
      gridTemplateColumns: props.differentDropOff
        ? "repeat(2, 1fr) repeat(2, 12rem)"
        : "1fr repeat(2, 12rem)",
      gridGap: jssVariables.half_gap,
      ..._.get(props, "customStyles.formContainer.smallscreen", {}),
    }),
    searchBtnContainer: (props) => ({
      ..._.get(props, "customStyles.searchBtnContainer.smallscreen", {}),
    }),
    checkbox: (props) => ({
      ..._.get(props, "customStyles.checkbox.smallscreen", {}),
    }),
    mobileOnly: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.mobileOnly.smallscreen", {}),
    }),
    desktopOnly: (props) => ({
      display: "grid",
      gridTemplateColumns: "1fr repeat(2, 12rem)",
      gridGap: jssVariables.half_gap,
      ..._.get(props, "customStyles.desktopOnly.smallscreen", {}),
    }),
    desktop: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.desktop.smallscreen", {}),
    }),
    searchBtnContr: (props) => ({
      padding: 0,
      ..._.get(props, "customStyles.searchBtnContr.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    carRentalSearchForm: (props) => ({
      gridTemplateColumns: "79rem max-content",
      ..._.get(props, "customStyles.carRentalSearchForm.bigscreen", {}),
    }),
    formContainer: (props) => ({
      gridTemplateColumns: props.differentDropOff
        ? "repeat(2, 1fr) repeat(2, 20rem)"
        : "1fr repeat(2, 20rem)",
      ..._.get(props, "customStyles.formContainer.bigscreen", {}),
    }),
  },
});
export const CarRentalSearchForm = ({ customStyles }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [differentDropOffLcl, setDifferentDropOffLcl] = useState(false);

  const [initSearch] = useCarRentalsInitSearch({
    setLoadingSearchBtn: setIsSubmitting,
  });
  const { i18n } = useContext(TranslationContext);
  const { t } = useContext(TranslationContext);

  const dispatch = useDispatch();

  const search_form = useSelector((state) => state.CarRentalSearchForm);
  const classes = styles({
    differentDropOff: differentDropOffLcl,
    customStyles,
  });

  const onClearState = useCallback(() => {
    dispatch(carRentalSearchFormReset());
    dispatch(carRentalSearchResultsReset());
    dispatch(carRentalFiltersReset());
    dispatch(selectedCarRentalReset());
  }, [dispatch]);

  useEffect(() => {
    onClearState();
  }, []);

  return (
    <Formik
      initialValues={{
        ...search_form,
        pick_up_date: [search_form.pick_up_date],
        drop_off_date: [search_form.drop_off_date],
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      // validate={async (values) => {
      //   if (values.differentDropOff !== differentDropOffLcl) {
      //     setDifferentDropOffLcl(values.differentDropOff);
      //   }
      // }}
      onSubmit={async (values) => {
        if (!isSubmitting) {
          setIsSubmitting(true);
          const isValid = await validateCarRentalSearchForm(values, t);
          if (!isValid) {
            setIsSubmitting(false);
            return;
          }
          const pick_up_date = DateTime.fromISO(values.pick_up_date).toFormat(
            "yyyy-MM-dd'T'HH:mm:ss",
          );
          const drop_off_date = DateTime.fromISO(values.drop_off_date).toFormat(
            "yyyy-MM-dd'T'HH:mm:ss",
          );
          const form = {
            ...values,
            pick_up_date,
            drop_off_date,
            drop_off_point:
              values.differentDropOff && !_.isEmpty(values.drop_off_point)
                ? values.drop_off_point
                : {
                    drop_off_point_code: values.pick_up_point.pick_up_point_code,
                    drop_off_point_meta: values.pick_up_point.pick_up_point_meta,
                  },
            language: i18n.language.split("-")[0],
          };
          initSearch(form);
        }
      }}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form className={classes.carRentalSearchForm}>
            <div className={classes.mobileOnly}>
              <div className={classes.span2}>
                <Toggle
                  name="differentDropOff"
                  label={t("differentDropOff")}
                  customStyles={customStyles}
                />
              </div>
            </div>
            <div className={classes.formContainer}>
              <POIAutoCompleteInputField
                id="carRentalPickUpPoint"
                label={t("pickUpPoint")}
                name={"pick_up_point"}
                types={["DEST"]}
                placeholder={"----"}
                customStyles={{
                  singleValue: differentDropOffLcl
                    ? pointInputGroupStyles.differentDropOff.singleValue
                    : pointInputGroupStyles.sameDropOff.singleValue,
                  ..._.get(customStyles, "inputGroupField", {}),
                }}
              />
              {differentDropOffLcl && (
                <POIAutoCompleteInputField
                  id="carRentalDropOffPoint"
                  label={t("dropOffPoint")}
                  name={"drop_off_point"}
                  types={["DEST"]}
                  placeholder={"----"}
                  customStyles={{
                    ...pointInputGroupStyles.differentDropOff,
                    ..._.get(customStyles, "inputGroupField", {}),
                  }}
                />
              )}
              <DatepickerField
                id="carRentalPickUpDatepicker"
                label={t("pickUpDate")}
                name="pick_up_date"
                selectsRange={false}
                dateFormat="MMM d, yyyy h:mm aa"
                minDate={DateTime.now().toISODate()}
                maxDate={DateTime.now().plus({ days: 365 }).toISO()}
                placeholder={"Mon DD, YYYY h:mm aa"}
                withPortal={true}
                showTimeSelect={true}
                customStyles={_.get(customStyles, "inputGroupField", {})}
                extraStyles={{}}
              />
              <DatepickerField
                id="carRentalDropOffDatepicker"
                label={t("dropOffDate")}
                name="drop_off_date"
                selectsRange={false}
                dateFormat="MMM d, yyyy h:mm aa"
                minDate={DateTime.now().toISODate()}
                maxDate={DateTime.now().plus({ days: 365 }).toISO()}
                placeholder={"Mon DD, YYYY h:mm aa"}
                withPortal={true}
                showTimeSelect={true}
                customStyles={_.get(customStyles, "inputGroupField", {})}
                extraStyles={{}}
              />
            </div>
            <div className={classes.mobileOnly}>
              <SelectField
                label={t("driverNationality")}
                name={"driver_nationality"}
                options={_.sortBy(
                  Object.entries(countries).map(([code, name]) => [code, `${name} (${code})`]),
                  (v) => v[1],
                )}
                customStyles={_.get(customStyles, "inputGroupField", {})}
                placeholder="----"
              />
              <DriverAge name="driver_age" />
            </div>
            <div className={classes.desktop} />
            <div className={classes.desktopOnly}>
              <Toggle
                id="differentDropOff"
                name="differentDropOff"
                label={t("differentDropOff")}
                customStyles={customStyles}
              />
              <SelectField
                id="carRentalDriverNationality"
                label={t("driverNationality")}
                name={"driver_nationality"}
                options={_.sortBy(
                  Object.entries(countries).map(([code, name]) => [code, `${name} (${code})`]),
                  (v) => v[1],
                )}
                customStyles={_.get(customStyles, "inputGroupField", {})}
                placeholder="----"
              />
              <DriverAge name="driver_age" id="driverAge" />
            </div>
            <div className={classes.searchBtnContr}>
              <SearchButton
                id="carRentalSearchBtn"
                text={t("search")}
                loading={isSubmitting}
                customStyles={_.get(customStyles, "searchBtn", {})}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
CarRentalSearchForm.defaultProps = {
  customStyles: {},
};
CarRentalSearchForm.propTypes = {
  customStyles: PropTypes.object.isRequired,
  setLoadingSearchBtn: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};
